// src/pages/CancerTeleconsultationHub.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Video, 
  CheckCircle2, 
  ArrowRight, 
  Clock, 
  DollarSign, 
  ChevronRight, 
  ChevronDown,
  Activity,
  Scissors,
  FileSearch,
  Building2,
  Stethoscope,
  Calendar,
  Lock,
  XCircle,
  HelpCircle,
  Users,
  ShieldAlert
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { TELECONSULTATION_HUB_DATA, TELECONSULTATION_SPOKES } from '@/data/teleconsultationData';
import Footer from '@/components/Footer';

export default function CancerTeleconsultationHub() {
  const [openFaq, setOpenFaq] = useState(null);
  const data = TELECONSULTATION_HUB_DATA;
  const pageUrl = `${SITE_ORIGIN}/cancer-teleconsultation`;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'Stethoscope': return <Stethoscope className="w-6 h-6 text-emerald-700" />;
      case 'Activity': return <Activity className="w-6 h-6 text-emerald-700" />;
      case 'ShieldAlert': return <ShieldAlert className="w-6 h-6 text-emerald-700" />;
      case 'Scissors': return <Scissors className="w-6 h-6 text-emerald-700" />;
      case 'FileSearch': return <FileSearch className="w-6 h-6 text-emerald-700" />;
      case 'Building2': return <Building2 className="w-6 h-6 text-emerald-700" />;
      default: return <CheckCircle2 className="w-6 h-6 text-emerald-700" />;
    }
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: data.title,
      description: data.description,
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      author: {
        '@type': 'Person',
        name: data.reviewer.name,
        jobTitle: data.reviewer.title,
        description: data.reviewer.credentials,
      },
      reviewedBy: {
        '@type': 'Person',
        name: data.reviewer.name,
        jobTitle: data.reviewer.title,
      },
      lastReviewed: '2026-09-24',
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Oncology Telemedicine Network',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Cancer Teleconsultation', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={data.metaTitle}
        description={data.description}
        path="/cancer-teleconsultation"
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">Cancer Teleconsultation</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/teleconsultation" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Book Video Visit ($199) <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-16 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            ASCO Telehealth Quality Compliant · HIPAA 256-Bit Encrypted
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            {data.h1}
          </h1>
          <p className="text-lg sm:text-2xl text-emerald-800 font-medium mb-6">
            {data.h1Sub}
          </p>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/90 leading-relaxed mb-8">
            Speak directly with board-certified medical, surgical, and radiation oncologists from the comfort of your home. Review complex pathology, verify staging and biomarker targets, and get clarity on difficult cancer treatment decisions within 24 to 48 hours.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link
              to="/teleconsultation"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-base transition shadow-md hover:shadow-lg"
            >
              Book Oncology Video Consult ($199) <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#specialty-spokes"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-gray-300 hover:border-forest-ink bg-white text-forest-ink font-semibold text-base transition"
            >
              Explore Disease-Specific Spokes
            </a>
          </div>

          {/* Quick Trust Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto text-left">
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Availability</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">Within 24–48 Hours</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Video className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Visit Length</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">30–45 Min Video</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Lock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Privacy</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">HIPAA 256-Bit Encrypted</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Transparent Fee</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">$199 Flat Upfront</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Medical Review Byline */}
      <section className="bg-white border-b border-border-mist py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold text-sm">
                MD
              </div>
              <div>
                <p className="font-semibold text-forest-ink">
                  Medically Reviewed by {data.reviewer.name}
                </p>
                <p className="text-gray-600 text-xs">
                  {data.reviewer.title} · {data.reviewer.credentials}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right text-gray-500 text-xs">
              <p>Updated: {data.reviewer.lastReviewed}</p>
              <p className="text-emerald-800 font-medium">{data.reviewer.guidelineRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Section 1: When to Book */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              When Should You Book an Oncology Video Consultation?
            </h2>
            <p className="text-charcoal/80 text-base">
              A virtual video consultation gives you dedicated, unhurried time with an independent cancer specialist to evaluate your care trajectory.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {data.whenToUse.map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs flex items-start gap-4">
                <div className="p-3 rounded-lg bg-emerald-50 flex-shrink-0">
                  {getIcon(item.icon)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-forest-ink mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-charcoal/80 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: Clinical Scope Matrix */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Clinical Scope: What Can & Cannot Be Done Via Telehealth
            </h2>
            <p className="text-charcoal/80 text-base">
              Clinical safety and transparency are our highest priorities. Here is how oncology telemedicine fits into your broader care continuum:
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border-mist shadow-xs bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-forest-ink text-white font-medium text-xs sm:text-sm uppercase tracking-wider">
                <tr>
                  <th scope="col" className="px-6 py-4">Clinical Domain</th>
                  <th scope="col" className="px-6 py-4">Telehealth Scope</th>
                  <th scope="col" className="px-6 py-4">Clinical Explanation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-mist">
                {data.whatCanBeEvaluated.map((row, idx) => {
                  const isFull = row.telehealthCapability === 'Full Evaluation';
                  return (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-cream-paper/40'}>
                      <td className="px-6 py-4 font-semibold text-forest-ink">
                        {row.feature}
                      </td>
                      <td className="px-6 py-4 font-medium">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isFull 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-amber-100 text-amber-900'
                        }`}>
                          {isFull ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> : <XCircle className="w-3.5 h-3.5 text-amber-700" />}
                          {row.telehealthCapability}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs sm:text-sm text-charcoal/80">
                        {row.detail}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 3: Transparent Pricing */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Transparent, Flat-Fee Pricing
            </h2>
            <p className="text-charcoal/80 text-base">
              Predictable upfront healthcare pricing without hidden facility fees or surprise out-of-network bills.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {data.transparentPricing.map((tier, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-8 flex flex-col justify-between transition ${
                  tier.popular 
                    ? 'bg-white border-2 border-emerald-600 shadow-lg relative' 
                    : 'bg-gray-50 border border-gray-200 text-gray-500'
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold tracking-wide uppercase">
                    ByOnco Direct Access
                  </span>
                )}
                <div>
                  <h3 className={`text-xl font-bold mb-1 ${tier.traditional ? 'text-gray-700' : 'text-forest-ink'}`}>
                    {tier.service}
                  </h3>
                  <div className="my-4">
                    <span className={`text-4xl font-extrabold ${tier.traditional ? 'text-gray-700' : 'text-forest-ink'}`}>
                      {tier.price}
                    </span>
                    <p className={`text-xs font-medium mt-1 ${tier.popular ? 'text-emerald-700 font-semibold' : 'text-gray-500'}`}>
                      Duration: {tier.duration}
                    </p>
                  </div>

                  <ul className="space-y-3 my-6 text-xs sm:text-sm">
                    {tier.includes.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          tier.popular ? 'text-emerald-600' : 'text-gray-400'
                        }`} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-border-mist">
                  {tier.traditional ? (
                    <p className="text-xs text-center text-gray-500 italic">US Academic Hospital Average</p>
                  ) : (
                    <Link
                      to={tier.ctaLink}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-sm transition shadow-sm"
                    >
                      {tier.ctaText} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4: 4-Step Process Walkthrough */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              How Your Oncology Video Consultation Works
            </h2>
            <p className="text-charcoal/80 text-base">
              Four simple steps from booking to receiving your post-visit clinical roadmap.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.howItWorksSteps.map((step, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-cream-paper/70 border border-border-mist">
                <span className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm mb-4">
                  {step.step}
                </span>
                <h3 className="font-bold text-forest-ink mb-2">{step.title}</h3>
                <p className="text-xs text-charcoal/80 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: Specialty Spokes Directory */}
        <section id="specialty-spokes">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Specialized Teleconsultation Guides
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-forest-ink mt-3 mb-3">
              Explore Virtual Consultations by Diagnosis & Visit Type
            </h2>
            <p className="text-charcoal/80 text-base">
              Detailed clinical guides outlining what records to gather, key molecular targets evaluated, and top questions for your virtual visit.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TELECONSULTATION_SPOKES.map((spoke) => (
              <Link
                key={spoke.slug}
                to={spoke.path}
                className="group p-6 rounded-2xl bg-white border border-border-mist hover:border-emerald-600 hover:shadow-md transition flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md">
                      {spoke.shortName}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition" />
                  </div>
                  <h3 className="text-lg font-bold text-forest-ink group-hover:text-emerald-800 transition mb-2">
                    {spoke.h1}
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal/80 line-clamp-3 mb-4 leading-relaxed">
                    {spoke.clinicalSummary}
                  </p>
                </div>

                <div className="pt-4 border-t border-border-mist flex items-center justify-between text-xs text-gray-500">
                  <span>Reviewed by {spoke.reviewer.name.split(',')[0]}</span>
                  <span className="font-semibold text-emerald-800 group-hover:underline">Read Guide</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Section 6: FAQs */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions About Oncology Teleconsultations
            </h2>
            <p className="text-charcoal/80 text-base">
              Clear clinical and legal answers regarding telemedicine prescriptions, multi-state family attendance, and second opinions.
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {data.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="rounded-xl border border-border-mist bg-white overflow-hidden transition shadow-xs"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 font-bold text-base sm:text-lg text-forest-ink hover:text-emerald-800 transition"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-emerald-700 flex-shrink-0 rotate-180 transition-transform" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 text-sm sm:text-base text-charcoal/85 leading-relaxed border-t border-cream-paper bg-cream-paper/30">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 7: Contextual CTA */}
        <section className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-12 text-center shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-3xl mx-auto">
            <span className="inline-block px-3.5 py-1.5 rounded-full bg-emerald-800/60 text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
              Get Answers Without Leaving Home
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-white mb-4 leading-tight">
              Schedule Your 1-on-1 Oncology Video Consultation Today
            </h2>
            <p className="text-emerald-100/90 text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
              Connect face-to-face with an experienced medical oncologist. Review your scans, discuss clinical trials, and leave with a clear, written roadmap.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-forest-ink hover:bg-emerald-50 font-bold text-base transition shadow-md"
              >
                Book Video Visit ($199) <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/cancer-second-opinion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/30 hover:border-white text-white font-medium text-base transition"
              >
                Explore Written Second Opinions
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
